import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, ChevronRight, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface ArticlesCopy {
  sectionTitle: string;
  sectionLead: string;
  allResourcesBtn: string;
  loadErrorText: string;
  retryBtn: string;
  noArticlesTitle: string;
  noArticlesDesc: string;
  viewResourcesBtn: string;
  readArticleBtn: string;
  defaultArticleAlt: string;
}
const ARTICLES_COPY: Record<Lang, ArticlesCopy> = {
  en: {
    sectionTitle: "Technical Articles & Insights",
    sectionLead: "Engineering guides, material specifications, and technical analysis on stainless steel woven wire mesh and filtration media.",
    allResourcesBtn: "All Resources",
    loadErrorText: "Unable to load technical articles at this time.",
    retryBtn: "Retry Loading",
    noArticlesTitle: "No Articles Published Yet",
    noArticlesDesc: "Our engineering technical whitepapers and filtration guides will be available shortly. Explore our full technical documentation and calculation guides in the meantime.",
    viewResourcesBtn: "View Technical Resources",
    readArticleBtn: "Read Article",
    defaultArticleAlt: "Technical Article"
  },
  zh: {
    sectionTitle: "技术文章与工程见解",
    sectionLead: "关于不锈钢编织丝网与分离过滤介质的工程指南、材料规范和技术深度分析。",
    allResourcesBtn: "查看全部技术资源",
    loadErrorText: "暂无法加载技术文章，请稍后重试。",
    retryBtn: "重新加载",
    noArticlesTitle: "暂无发布文章",
    noArticlesDesc: "工程技术白皮书和过滤选型指南即将上线。在此期间，欢迎查阅我们的全套技术文档与计算工具。",
    viewResourcesBtn: "查看技术资源中心",
    readArticleBtn: "阅读文章",
    defaultArticleAlt: "技术文章"
  },
  es: {
    sectionTitle: "Artículos técnicos y perspectivas",
    sectionLead: "Guías de ingeniería, especificaciones de materiales y análisis técnico sobre mallas tejidas de acero inoxidable y medios de filtración.",
    allResourcesBtn: "Todos los recursos",
    loadErrorText: "No es posible cargar los artículos técnicos en este momento.",
    retryBtn: "Reintentar carga",
    noArticlesTitle: "Aún no hay artículos publicados",
    noArticlesDesc: "Nuestros informes técnicos y guías de filtración estarán disponibles en breve. Consulte nuestra documentación técnica y guías de cálculo.",
    viewResourcesBtn: "Ver recursos técnicos",
    readArticleBtn: "Leer artículo",
    defaultArticleAlt: "Artículo técnico"
  },
  de: {
    sectionTitle: "Fachartikel & Technische Einblicke",
    sectionLead: "Ingenieurleitfäden, Materialspezifikationen und technische Analysen zu Edelstahl-Drahtgewebe und Filtrationsmedien.",
    allResourcesBtn: "Alle Ressourcen",
    loadErrorText: "Fachartikel können derzeit nicht geladen werden.",
    retryBtn: "Erneut laden",
    noArticlesTitle: "Noch keine Artikel veröffentlicht",
    noArticlesDesc: "Unsere technischen Whitepaper und Filtrationsleitfäden stehen in Kürze zur Verfügung. Entdecken Sie in der Zwischenzeit unsere Berechnungsleitfäden.",
    viewResourcesBtn: "Technische Ressourcen ansehen",
    readArticleBtn: "Artikel lesen",
    defaultArticleAlt: "Fachartikel"
  },
  fr: {
    sectionTitle: "Articles techniques & perspectives",
    sectionLead: "Guides d'ingénierie, spécifications de matériaux et analyses techniques sur les toiles métalliques en acier inoxydable et milieux filtrants.",
    allResourcesBtn: "Toutes les ressources",
    loadErrorText: "Impossible de charger les articles techniques pour le moment.",
    retryBtn: "Réessayer",
    noArticlesTitle: "Aucun article publié pour le moment",
    noArticlesDesc: "Nos livres blancs techniques et guides de filtration seront bientôt disponibles. Découvrez entre-temps l'ensemble de nos documentations techniques.",
    viewResourcesBtn: "Consulter les ressources techniques",
    readArticleBtn: "Lire l'article",
    defaultArticleAlt: "Article technique"
  },
  pt: {
    sectionTitle: "Artigos técnicos e perspectivas",
    sectionLead: "Guias de engenharia, especificações de materiais e análises técnicas sobre telas tecidas de aço inoxidável e meios filtrantes.",
    allResourcesBtn: "Todos os recursos",
    loadErrorText: "Não foi possível carregar os artigos técnicos no momento.",
    retryBtn: "Tentar novamente",
    noArticlesTitle: "Nenhum artigo publicado ainda",
    noArticlesDesc: "Nossos whitepapers técnicos de engenharia e guias de filtração estarão disponíveis em breve. Explore nossa documentação técnica completa.",
    viewResourcesBtn: "Ver recursos técnicos",
    readArticleBtn: "Ler artigo",
    defaultArticleAlt: "Artigo técnico"
  }
};
function ArticlesConsumer() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, ARTICLES_COPY);
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="rounded-lg border border-border bg-card p-6 space-y-4">
            <div className="h-48 w-full bg-muted rounded-lg" />
            <div className="h-4 w-1/3 bg-muted rounded" />
            <div className="h-6 w-3/4 bg-muted rounded" />
            <div className="h-16 w-full bg-muted rounded" />
            <div className="h-9 w-28 bg-muted rounded" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-border bg-card p-8 text-center space-y-4">
        <p className="text-muted-foreground text-sm">
          {c.loadErrorText}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          {c.retryBtn}
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="rounded-lg border border-border bg-card p-12 text-center space-y-4">
        <FileText className="size-12 text-muted-foreground mx-auto" />
        <h3 className="mf-h3 text-foreground">{c.noArticlesTitle}</h3>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">
          {c.noArticlesDesc}
        </p>
        <Button asChild variant="outline">
          <Link to="/TechnicalResourcesPage">{c.viewResourcesBtn}</Link>
        </Button>
      </div>;
  }
  const localeMap: Record<Lang, string> = {
    en: "en-US",
    zh: "zh-CN",
    es: "es-ES",
    de: "de-DE",
    fr: "fr-FR",
    pt: "pt-BR"
  };
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {posts.map((post, i) => <Card key={post.id || i} data-index={i} className="group flex flex-col justify-between overflow-hidden rounded-lg border-border hover:border-primary/40 transition-all duration-300">
          <div>
            {post.featuredImage ? <div className="mf-card-media relative mf-ratio-card w-full overflow-hidden">
                <img src={post.featuredImage} alt={post.title || c.defaultArticleAlt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </div> : <div className="mf-card-media relative mf-ratio-card w-full bg-muted/60 flex items-center justify-center">
                <FileText className="size-12 text-muted-foreground/50" />
              </div>}
            <CardHeader className="space-y-2.5 pt-6">
              <div className="flex items-center gap-3 text-[13px] text-muted-foreground">
                {post.date && <time className="flex items-center gap-1" dateTime={post.date}>
                    <SafeHydrate><Calendar className="size-3.5 text-muted-foreground" /></SafeHydrate>
                    {new Date(post.date).toLocaleDateString(localeMap[lang] || "en-US", {
                month: "short",
                day: "numeric",
                year: "numeric"
              })}
                  </time>}
                {post.categories && post.categories.length > 0 && <Badge variant="outline" className="bg-muted text-foreground text-[13px] font-normal">
                    {post.categories[0].name}
                  </Badge>}
              </div>
              <CardTitle className="mf-h3 tracking-tight text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                {post.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {post.excerpt && <div className="text-sm text-muted-foreground line-clamp-3 leading-relaxed" dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} />}
            </CardContent>
          </div>
          <CardFooter className="pt-2 pb-6">
            <Button asChild variant="link" className="px-0 text-primary font-medium group-hover:underline inline-flex items-center gap-1 text-sm">
              <Link to={post.link || "#"}>
                {c.readArticleBtn}
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function Articles() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, ARTICLES_COPY);
  return <section id="articles" data-nav="light" className="bg-muted/50 text-foreground mf-section border-b border-border">
      <div className="mf-container">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl space-y-3">
            <h2 className="mf-h2 text-foreground">
              {c.sectionTitle}
            </h2>
            <p className="mf-lead text-muted-foreground">
              {c.sectionLead}
            </p>
          </div>
          <div>
            <Button asChild variant="outline">
              <Link to="/TechnicalResourcesPage" className="inline-flex items-center gap-2">
                {c.allResourcesBtn}
                <ChevronRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <ArticlesConsumer />
        </PostsProvider>
      </div>
    </section>;
}